
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, User, Shield, Eye, Share2, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { Auth as FirebaseAuth } from '@/integrations/firebase/config';
import { authService } from '@/integrations/firebase/authService';
import { toast } from 'sonner';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if user is logged in
  const { data: user, refetch } = useQuery({
    queryKey: ['authUser'],
    queryFn: async () => {
      return FirebaseAuth.currentUser;
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      toast.success("Signed out successfully");
      refetch();
      navigate('/');
    } catch (error) {
      toast.error("Error signing out");
    }
  };

  const navLinks = [
    { name: 'Dashboard', path: '/dashboard', icon: User, requireAuth: true },
    { name: 'Create Profile', path: '/profile', icon: Shield, requireAuth: true },
    { name: 'Preview', path: '/preview', icon: Eye, requireAuth: true },
  ];

  // Filter links based on authentication status
  const filteredNavLinks = navLinks.filter(link => !link.requireAuth || user);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'py-3 glass' : 'py-5 bg-transparent'}`}>
      <div className="container mx-auto px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-2"
          >
            <span className="font-display text-primary text-2xl font-bold">pvt.bio</span>
          </motion.div>
        </Link>
        
        <nav className="hidden md:flex items-center space-x-1">
          {filteredNavLinks.map((link) => {
            const Icon = link.icon;
            return (
              <motion.div
                key={link.name}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <Link to={link.path}>
                  <Button 
                    variant={location.pathname === link.path ? "default" : "ghost"}
                    className="flex items-center gap-2 px-4 py-2 rounded-full"
                  >
                    <Icon size={16} />
                    <span>{link.name}</span>
                  </Button>
                </Link>
              </motion.div>
            );
          })}
          
          {user ? (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="ml-2"
            >
              <Button 
                variant="outline" 
                className="rounded-full flex items-center gap-2"
                onClick={handleSignOut}
              >
                <LogOut size={16} />
                <span>Sign Out</span>
              </Button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              className="ml-2"
            >
              <Link to="/auth">
                <Button 
                  variant="default" 
                  className="rounded-full flex items-center gap-2"
                >
                  <User size={16} />
                  <span>Sign In</span>
                </Button>
              </Link>
            </motion.div>
          )}
        </nav>
        
        <div className="md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>
      </div>
      
      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden glass mt-2 overflow-hidden"
        >
          <div className="container mx-auto px-6 py-4 flex flex-col space-y-3">
            {filteredNavLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link 
                  key={link.name} 
                  to={link.path}
                  className={`flex items-center gap-3 p-3 rounded-lg ${location.pathname === link.path ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'}`}
                >
                  <Icon size={18} />
                  <span>{link.name}</span>
                </Link>
              );
            })}
            
            {user ? (
              <Button 
                variant="outline" 
                className="flex items-center justify-start gap-3 p-3"
                onClick={handleSignOut}
              >
                <LogOut size={18} />
                <span>Sign Out</span>
              </Button>
            ) : (
              <Link 
                to="/auth"
                className="flex items-center gap-3 bg-primary text-white p-3 rounded-lg"
              >
                <User size={18} />
                <span>Sign In</span>
              </Link>
            )}
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default Header;
